import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  Snackbar,
  Skeleton,
  Stack,
} from '@mui/material';
import {
  Refresh,
  ArrowBack,
  PlayArrow,
  ThumbUp,
  Paid,
  Cancel,
  Payments,
  Search,
  AccountBalance,
  ReceiptLong,
  OpenInNew,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  CheckCircle,
  Lock,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { hrService, PayrollRunStatus } from '../../services/hrService';
import hrTimePayrollService from '../../services/hrTimePayrollService';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const toNum = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const money = (v: number | string | null | undefined): string => {
  const n = toNum(v);
  return n === null ? '—' : formatCurrency(n);
};

const moneyCompact = (v: number | string | null | undefined): string => {
  const n = toNum(v);
  return n === null ? '—' : formatIndianNumber(n);
};

const days = (v: number | string | null | undefined): string => {
  const n = toNum(v);
  return n === null ? '—' : `${n}`;
};

const fmtDateTime = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const statusTone: Record<PayrollRunStatus, StatusTone> = {
  DRAFT: 'neutral',
  PROCESSING: 'info',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'success',
  PAID: 'success',
  CANCELLED: 'error',
};

const statusLabel: Record<PayrollRunStatus, string> = {
  DRAFT: 'Draft',
  PROCESSING: 'Processing',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
};

const lifecycleStages: PayrollRunStatus[] = ['DRAFT', 'PROCESSING', 'PENDING_APPROVAL', 'APPROVED', 'PAID'];

// ---------------------------------------------------------------------------
// Types for the workspace payload (defensive — every field may be absent)
// ---------------------------------------------------------------------------

interface WorkspaceRun {
  id: number;
  run_code: string;
  period_month: number;
  period_year: number;
  status: PayrollRunStatus;
  employee_count: number;
  gross_total: number | string;
  deduction_total: number | string;
  net_total: number | string;
  journal_entry_id: number | null;
  journal_entry_number: string | null;
  posted_at: string | null;
}

interface WorkspaceStatutory {
  grossTotal: number | string;
  basicTotal: number | string;
  employeePf: number | string;
  employerPf: number | string;
  employeeEsi: number | string;
  employerEsi: number | string;
  tds: number | string;
  professionalTax: number | string;
  otherDeductions: number | string;
  reimbursements: number | string;
  loanDeduction: number | string;
  netPay: number | string;
  employeeCount: number;
}

interface PreviousRunSummary {
  id: number;
  run_code: string;
  period_month: number;
  period_year: number;
  employee_count: number;
  gross_total: number | string;
  net_total: number | string;
}

interface WorkspaceVariance {
  previous: PreviousRunSummary | null;
  grossDelta: number | string | null;
  netDelta: number | string | null;
  headcountDelta: number | string | null;
  grossDeltaPct: number | string | null;
  netDeltaPct: number | string | null;
}

interface DeptCost {
  department: string;
  headcount: number | string;
  gross: number | string;
  net: number | string;
}

interface WorkspacePayslip {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_code: string;
  department: string | null;
  basic: number | string;
  hra: number | string;
  allowances: number | string;
  overtime_pay: number | string;
  gross: number | string;
  pf_employee: number | string;
  esi_employee: number | string;
  tds: number | string;
  professional_tax: number | string;
  other_deductions: number | string;
  loan_deduction: number | string;
  reimbursements: number | string;
  employer_pf: number | string;
  employer_esi: number | string;
  net_pay: number | string;
  days_paid: number | string;
  lop_days: number | string;
}

interface WorkspaceGl {
  posted: boolean;
  journalEntryId: number | null;
  journalEntryNumber: string | null;
  postedAt: string | null;
}

interface WorkspacePayload {
  run: WorkspaceRun;
  statutory: WorkspaceStatutory;
  variance: WorkspaceVariance;
  costByDepartment: DeptCost[];
  payslips: WorkspacePayslip[];
  gl: WorkspaceGl;
}

type PendingLifecycleAction = 'process' | 'approve' | 'paid' | 'cancel';

const lifecycleCopy: Record<
  PendingLifecycleAction,
  { title: string; body: string; confirmLabel: string; color: 'primary' | 'success' | 'error' }
> = {
  process: {
    title: 'Process Payroll Run',
    body:
      'This generates payslips for every active employee from CTC and this period’s attendance, replacing any existing payslips on this run. This cannot be undone from here.',
    confirmLabel: 'Process Payroll',
    color: 'primary',
  },
  approve: {
    title: 'Approve Payroll Run',
    body: 'Marks this run as approved, ready for payment.',
    confirmLabel: 'Approve',
    color: 'success',
  },
  paid: {
    title: 'Mark Payroll Run as Paid',
    body: 'Marks this run as paid. Only do this once salaries have actually been disbursed.',
    confirmLabel: 'Mark as Paid',
    color: 'success',
  },
  cancel: {
    title: 'Cancel Payroll Run',
    body: 'Cancels this draft run. It will no longer be processable.',
    confirmLabel: 'Cancel Run',
    color: 'error',
  },
};

// ---------------------------------------------------------------------------
// Small presentational bits
// ---------------------------------------------------------------------------

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
    {children}
  </Typography>
);

const DeltaChip: React.FC<{ value: number | string | null | undefined; pct?: number | string | null; money?: boolean }> = ({
  value,
  pct,
  money: isMoney,
}) => {
  const n = toNum(value);
  const p = toNum(pct ?? null);
  if (n === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const tone = n > 0 ? 'success.main' : n < 0 ? 'error.main' : 'text.secondary';
  const Icon = n > 0 ? TrendingUp : n < 0 ? TrendingDown : TrendingFlat;
  const display = isMoney ? moneyCompact(Math.abs(n)) : `${Math.abs(n)}`;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Icon sx={{ fontSize: 18, color: tone }} />
      <Typography variant="body2" sx={{ fontWeight: 700, color: tone, fontVariantNumeric: 'tabular-nums' }}>
        {n > 0 ? '+' : n < 0 ? '-' : ''}
        {display}
      </Typography>
      {p !== null && (
        <Typography variant="caption" sx={{ color: tone }}>
          ({p > 0 ? '+' : ''}
          {p.toFixed(1)}%)
        </Typography>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PayrollRunWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const runId = id ? Number(id) : NaN;
  const navigate = useNavigate();

  const [data, setData] = useState<WorkspacePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingAction, setPendingAction] = useState<PendingLifecycleAction | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [postConfirmOpen, setPostConfirmOpen] = useState(false);
  const [postBusy, setPostBusy] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  const [search, setSearch] = useState('');

  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const load = useCallback(() => {
    if (!Number.isFinite(runId)) {
      setError('Invalid payroll run id');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    hrTimePayrollService
      .runWorkspace(runId)
      .then((res: WorkspacePayload) => setData(res))
      .catch((err: any) => setError(err.message || 'Failed to load the payroll run workspace'))
      .finally(() => setLoading(false));
  }, [runId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = data?.run;
  const stageIndex = run ? lifecycleStages.indexOf(run.status) : -1;

  const handleConfirmLifecycle = async () => {
    if (!run || !pendingAction) return;
    setActionBusy(true);
    setActionError(null);
    try {
      if (pendingAction === 'process') {
        await hrService.processPayrollRun(run.id);
      } else if (pendingAction === 'approve') {
        await hrService.updatePayrollRunStatus(run.id, 'APPROVED');
      } else if (pendingAction === 'paid') {
        await hrService.updatePayrollRunStatus(run.id, 'PAID');
      } else if (pendingAction === 'cancel') {
        await hrService.updatePayrollRunStatus(run.id, 'CANCELLED');
      }
      setPendingAction(null);
      load();
    } catch (err: any) {
      setActionError(err.message || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const handlePostToFinance = async () => {
    if (!run) return;
    setPostBusy(true);
    setPostError(null);
    try {
      const res: any = await hrTimePayrollService.postPayrollRun(run.id);
      const je = res?.journalEntry;
      setPostConfirmOpen(false);
      setToast({
        message: je
          ? `Posted ${je.entry_number} — total debit ${money(je.total_debit)} = total credit ${money(je.total_credit)}`
          : 'Payroll run posted to the general ledger.',
        severity: 'success',
      });
      load();
    } catch (err: any) {
      setPostError(err.message || 'Failed to post this run to the general ledger');
    } finally {
      setPostBusy(false);
    }
  };

  const filteredPayslips = useMemo(() => {
    const rows = data?.payslips || [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (p) => p.employee_name?.toLowerCase().includes(q) || p.employee_code?.toLowerCase().includes(q)
    );
  }, [data, search]);

  // ---- Loading skeleton ----
  if (loading && !data) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/hr/payroll')} sx={{ mb: 2 }}>
          Back to Payroll
        </Button>
        <Skeleton variant="text" width={280} height={44} />
        <Skeleton variant="text" width={180} />
        <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={92} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={220} />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/hr/payroll')} sx={{ mb: 2 }}>
          Back to Payroll
        </Button>
        <ErrorState message={error} onRetry={load} />
      </Box>
    );
  }

  if (!data || !run) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/hr/payroll')} sx={{ mb: 2 }}>
          Back to Payroll
        </Button>
        <TableEmptyState title="Payroll run not found" />
      </Box>
    );
  }

  const canPost = run.status === 'APPROVED' || run.status === 'PAID';

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back to Payroll
      </Button>

      <PageHeader
        title={run.run_code}
        qualifier={`— ${monthNames[run.period_month - 1]} ${run.period_year}`}
        subtitle={`${run.employee_count} employee${run.employee_count === 1 ? '' : 's'} on this run`}
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={load} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            {run.status === 'DRAFT' && (
              <>
                <Button variant="outlined" color="error" startIcon={<Cancel />} onClick={() => setPendingAction('cancel')}>
                  Cancel Run
                </Button>
                <Button variant="contained" startIcon={<PlayArrow />} onClick={() => setPendingAction('process')}>
                  Process Payroll
                </Button>
              </>
            )}
            {run.status === 'PENDING_APPROVAL' && (
              <Button variant="contained" color="success" startIcon={<ThumbUp />} onClick={() => setPendingAction('approve')}>
                Approve
              </Button>
            )}
            {run.status === 'APPROVED' && (
              <Button variant="contained" color="success" startIcon={<Paid />} onClick={() => setPendingAction('paid')}>
                Mark as Paid
              </Button>
            )}
          </>
        }
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Lifecycle stepper */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {run.status === 'CANCELLED' ? (
          <StatusDot label="Cancelled" tone="error" />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {lifecycleStages.map((stage, i) => {
              const reached = i <= stageIndex;
              return (
                <React.Fragment key={stage}>
                  {i > 0 && <Box sx={{ width: 24, height: 2, backgroundColor: reached ? 'success.main' : 'divider' }} />}
                  <Chip
                    size="small"
                    label={statusLabel[stage]}
                    color={i === stageIndex ? 'primary' : reached ? 'success' : 'default'}
                    variant={i === stageIndex ? 'filled' : reached ? 'filled' : 'outlined'}
                  />
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* KPI band */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Employees" value={run.employee_count} icon={<Payments />} color="#0284c7" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Gross" value={moneyCompact(run.gross_total)} icon={<Payments />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Total Deductions" value={moneyCompact(run.deduction_total)} icon={<Payments />} color="#dc2626" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Net Pay" value={moneyCompact(run.net_total)} icon={<Payments />} color="#7c3aed" />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Statutory summary */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <SectionTitle>Statutory Summary</SectionTitle>
            <Grid container spacing={1.5}>
              {[
                ['Employee PF', data.statutory.employeePf],
                ['Employer PF', data.statutory.employerPf],
                ['PF Payable', (toNum(data.statutory.employeePf) ?? 0) + (toNum(data.statutory.employerPf) ?? 0)],
                ['Employee ESI', data.statutory.employeeEsi],
                ['Employer ESI', data.statutory.employerEsi],
                ['TDS', data.statutory.tds],
                ['Professional Tax', data.statutory.professionalTax],
                ['Other Deductions', data.statutory.otherDeductions],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6, sm: 3 }} key={label as string}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }} noWrap title={money(value as any)}>
                      {moneyCompact(value as any)}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Variance panel */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <SectionTitle>Variance vs Prior Period</SectionTitle>
            {!data.variance.previous ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                First run — no prior period to compare.
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  vs {data.variance.previous.run_code} ({monthNames[data.variance.previous.period_month - 1]}{' '}
                  {data.variance.previous.period_year})
                </Typography>
                <Stack spacing={1.25}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Gross
                    </Typography>
                    <DeltaChip value={data.variance.grossDelta} pct={data.variance.grossDeltaPct} money />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Net
                    </Typography>
                    <DeltaChip value={data.variance.netDelta} pct={data.variance.netDeltaPct} money />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Headcount
                    </Typography>
                    <DeltaChip value={data.variance.headcountDelta} />
                  </Box>
                </Stack>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Post to Finance */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: data.gl.posted ? 'success.main' : 'divider',
          background: data.gl.posted ? 'linear-gradient(160deg, #05966912 0%, transparent 60%)' : undefined,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                backgroundColor: data.gl.posted ? 'success.main' : 'action.selected',
                color: data.gl.posted ? '#fff' : 'text.secondary',
                flexShrink: 0,
              }}
            >
              {data.gl.posted ? <CheckCircle /> : <AccountBalance />}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{data.gl.posted ? 'Posted to GL' : 'Post to Finance'}</Typography>
              {data.gl.posted ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    color="success"
                    icon={<ReceiptLong />}
                    label={data.gl.journalEntryNumber || `#${data.gl.journalEntryId}`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Posted {fmtDateTime(data.gl.postedAt)}
                  </Typography>
                </Stack>
              ) : canPost ? (
                <Typography variant="body2" color="text.secondary">
                  Creates one balanced salary journal — Dr Salaries + employer PF/ESI, Cr Net Payable + PF/ESI/TDS/PT
                  payable.
                </Typography>
              ) : (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Lock sx={{ fontSize: 15, color: 'text.disabled' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Approve this run before posting to the general ledger.
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>

          {data.gl.posted ? (
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => navigate(data.gl.journalEntryId ? `/finance/journal/${data.gl.journalEntryId}` : '/finance/journal')}
            >
              View Journal
            </Button>
          ) : canPost ? (
            <Button variant="contained" color="success" startIcon={<AccountBalance />} onClick={() => { setPostError(null); setPostConfirmOpen(true); }}>
              Post to Finance
            </Button>
          ) : null}
        </Box>
      </Paper>

      {/* Cost by department */}
      <Box sx={{ mb: 3 }}>
        <SectionTitle>Cost by Department</SectionTitle>
        <TableSurface>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Department', 'Headcount', 'Gross', 'Net'].map((h, i) => (
                    <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.costByDepartment.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ border: 0 }}>
                      <TableEmptyState title="No department cost breakdown available" />
                    </TableCell>
                  </TableRow>
                ) : (
                  data.costByDepartment.map((d, i) => (
                    <TableRow key={`${d.department}-${i}`} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.25 }}>{d.department || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{days(d.headcount)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{moneyCompact(d.gross)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{moneyCompact(d.net)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      </Box>

      {/* Payslip register */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <SectionTitle>Payslip Register</SectionTitle>
          <TextField
            size="small"
            placeholder="Search employee name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
        </Box>
        <TableSurface>
          <TableContainer sx={{ overflowX: 'auto', maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    'Employee', 'Dept', 'Basic', 'HRA', 'Allow.', 'OT', 'Gross', 'PF', 'ESI', 'TDS', 'PT', 'Other',
                    'Loan Ded.', 'Net Pay', 'Days', 'LOP',
                  ].map((h, i) => (
                    <TableCell key={h} align={i <= 1 ? 'left' : 'right'} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPayslips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={16} sx={{ border: 0 }}>
                      <TableEmptyState
                        title={data.payslips.length === 0 ? 'No payslips yet' : 'No payslips match your search'}
                        hint={data.payslips.length === 0 && run.status === 'DRAFT' ? 'Process this run to generate payslips.' : undefined}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPayslips.map((p) => (
                    <TableRow key={p.id} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {p.employee_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {p.employee_code}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 140 }}>
                        <Typography variant="body2" noWrap title={p.department || undefined}>
                          {p.department || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.basic)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.hra)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.allowances)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.overtime_pay)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(p.gross)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.pf_employee)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.esi_employee)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.tds)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.professional_tax)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.other_deductions)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(p.loan_deduction)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'success.main' }}>
                        {money(p.net_pay)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{days(p.days_paid)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{days(p.lop_days)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      </Box>

      {/* Lifecycle confirm dialog */}
      <Dialog open={pendingAction !== null} onClose={() => (actionBusy ? undefined : setPendingAction(null))}>
        {pendingAction && (
          <>
            <DialogTitle>{lifecycleCopy[pendingAction].title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{lifecycleCopy[pendingAction].body}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingAction(null)} disabled={actionBusy}>
                Cancel
              </Button>
              <Button onClick={handleConfirmLifecycle} color={lifecycleCopy[pendingAction].color} variant="contained" disabled={actionBusy}>
                {actionBusy ? 'Working…' : lifecycleCopy[pendingAction].confirmLabel}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Post to finance confirm dialog */}
      <Dialog open={postConfirmOpen} onClose={() => (postBusy ? undefined : setPostConfirmOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Post Payroll Run to Finance</DialogTitle>
        <DialogContent>
          {postError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {postError}
            </Alert>
          )}
          <DialogContentText>
            This creates ONE balanced salary journal entry for <strong>{run.run_code}</strong>:
            <br />
            Dr Salaries &amp; Wages + Employer PF/ESI contribution
            <br />
            Cr Net Salary Payable + PF/ESI/TDS/Professional Tax/Other Deductions Payable
            <br />
            This action is idempotent — posting an already-posted run simply returns the existing journal entry.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostConfirmOpen(false)} disabled={postBusy}>
            Cancel
          </Button>
          <Button onClick={handlePostToFinance} color="success" variant="contained" disabled={postBusy} startIcon={<AccountBalance />}>
            {postBusy ? 'Posting…' : 'Post to Finance'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default PayrollRunWorkspace;
